import { Link } from 'react-router-dom';
import logoImg from '../assets/logo.png';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src={logoImg} alt="Logo" className="w-8 h-8" />
            <span className="font-semibold text-lg">Unified LLM Platform</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-10">How we handle your data on the Unified LLM Platform</p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">1. Privacy at a Glance</h2>
          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">General Information</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            The following information provides a simple overview of what happens to your personal data when you use
            this platform. Personal data is any data by which you can be personally identified. Detailed information
            on the subject of data protection can be found in our privacy policy below.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Who is responsible for data collection on this platform?</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Data processing on this platform is carried out by the platform operator. You can find the operator's
            contact details in the imprint of this platform.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">How do we collect your data?</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Your data is collected on the one hand by you providing it to us — for example, data you enter during
            registration or when submitting prompts to AI models. Other data is collected automatically by our IT
            systems when you visit the platform, including technical data such as your browser, operating system,
            and the time of your page visit.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            When you send messages to AI models, your prompts and the AI responses are transmitted to third-party
            AI providers (such as OpenAI, Anthropic, Google, Mistral, or DeepSeek) for processing. Each provider
            has its own privacy policy governing how they handle this data.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">What do we use your data for?</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Part of the data is collected to ensure error-free provision of the platform. Your conversation history
            is stored to provide continuity across sessions. Usage data may be used to analyze platform performance
            and improve the service.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">What rights do you have regarding your data?</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            You have the right at any time to receive free information about the origin, recipient, and purpose of
            your stored personal data. You also have the right to request the correction or deletion of this data.
            You can export all your data from the platform at any time via your profile settings. For further
            questions about data protection, you can contact us at any time.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">2. Responsible Party</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Imran Hussain<br />
            IH Software Services Provider<br />
            Goldäckerstraße 66<br />
            71144 Steinenbronn<br />
            Germany<br />
            Phone: +49 176 37130790<br />
            Email: <a href="mailto:info@imranhussain.de" className="text-blue-600 dark:text-blue-400 hover:underline">info@imranhussain.de</a>
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">3. Data We Collect</h2>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Account Data</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            When you register, we store your name, email address, and a hashed version of your password. This data
            is necessary to provide you with access to the platform and is stored for the duration of your account.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Conversation Data</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Your prompts and AI responses are stored in our database to allow you to review conversation history.
            Your messages are also transmitted to the AI provider you select (e.g. OpenAI, Anthropic) for
            processing. We do not use your conversation data to train AI models.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Usage Data</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            We collect anonymized usage statistics including token counts, model usage, and response times to
            monitor platform health and manage rate limits.
          </p>

          <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Session Data</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            When you log in, a session token is stored as an HTTP-only cookie. Sessions expire after 4 hours of
            inactivity. Session data is stored in Redis and/or our database and deleted upon logout or expiry.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">4. Third-Party AI Providers</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            This platform routes your prompts to one or more of the following AI providers depending on which model
            you select:
          </p>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-4">
            <li>OpenAI (openai.com) — GPT models</li>
            <li>Anthropic (anthropic.com) — Claude models</li>
            <li>Google (deepmind.google) — Gemini models</li>
            <li>Mistral AI (mistral.ai) — Mistral models</li>
            <li>DeepSeek (deepseek.com) — DeepSeek models</li>
            <li>Groq (groq.com) — Open-source models (Llama etc.)</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Each provider processes your prompts according to their own terms of service and privacy policies.
            We strongly recommend reviewing each provider's privacy policy before using their models for sensitive
            or confidential content.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">5. Data Retention</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Your personal data is retained for as long as your account is active. Conversation history is stored
            until you delete individual conversations or your entire account. You can export all your data at any
            time from your profile settings.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Audit logs are retained for up to 12 months for security and compliance purposes. Anonymized usage
            statistics may be retained indefinitely.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">6. Your Rights (GDPR)</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">Under the GDPR you have the following rights:</p>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
            <li><strong>Right of access</strong> — you can request a copy of all data we hold about you</li>
            <li><strong>Right to rectification</strong> — you can correct inaccurate data via your profile settings</li>
            <li><strong>Right to erasure</strong> — you can delete your account and all associated data</li>
            <li><strong>Right to data portability</strong> — you can export your data in JSON format from your profile</li>
            <li><strong>Right to restriction</strong> — you can request that we restrict processing of your data</li>
            <li><strong>Right to object</strong> — you can object to data processing based on legitimate interest</li>
          </ul>
          <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:info@imranhussain.de" className="text-blue-600 dark:text-blue-400 hover:underline">info@imranhussain.de</a>.
            You also have the right to lodge a complaint with a supervisory authority.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4">7. Security</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            This platform uses AES-256 encryption for sensitive data at rest, bcrypt for password hashing,
            HTTP-only secure session cookies, and TLS for all data in transit. Despite these measures, no
            internet-based transmission is completely secure. We cannot guarantee the absolute security of data
            transmitted to this platform.
          </p>
        </section>

        <p className="text-sm text-gray-400 dark:text-gray-500 mt-12">Last updated: March 2026</p>
      </main>

      <footer className="border-t border-gray-200 dark:border-gray-800 py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/imprint" className="hover:text-gray-900 dark:hover:text-white transition-colors">Imprint</Link>
          <Link to="/privacy" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
